import React, { useState, useEffect, useCallback, useRef } from 'react';
import Shell from './tuteur/Shell';
import Lecon from './Lecon';

// ── Rappel actif (page PERSONNELLE, propriétaire uniquement) ────────────────
// Accès via ?rappel. Le sens est donné en français, on TAPE le mot russe.
//
// C'est l'exercice qui manquait le plus : tout le quiz est en QCM, donc on
// n'entraînait que la reconnaissance, alors que le B2 exige environ 6 000 mots
// actifs. Reconnaître et produire sont deux compétences séparées.
//
// La correction est faite par le serveur : la réponse n'arrive jamais dans le
// navigateur avant la tentative, et la tolérance se règle à un seul endroit.

const TOKEN_KEY = 'motrusse_token';
const SERIF = "Georgia, 'Times New Roman', 'Noto Serif', serif";

const CoursRappel = () => (
  <Lecon id="rappel" titre="Avant de commencer : pourquoi taper plutôt que cocher">
    <p>
      Jusqu'ici le site te faisait <strong className="text-slate-200">reconnaître</strong> un mot parmi
      quatre. C'est utile, mais ce n'est pas la même compétence que le
      <strong className="text-slate-200"> produire</strong> sans amorce. Le B2 demande environ 6 000 mots
      actifs : des mots qui sortent tout seuls, pas des mots qu'on identifie quand on les voit.
    </p>
    <p className="text-slate-400">
      Ici tu tapes. C'est plus dur, c'est normal, et c'est exactement ce qui te manque : tu
      reconnais 4 600 mots et il te faut cinq minutes pour écrire une phrase de treize.
    </p>
    <p className="text-slate-400">
      Les mots proposés sont ceux que tu connais <strong className="text-slate-200">déjà</strong> passivement
      (au moins une réussite et une semaine d'intervalle). On ne te demande pas d'inventer,
      on te demande de faire remonter.
    </p>
    <p className="text-slate-500 text-[13px]">
      La correction tolère е pour ё, la casse et les espaces. À une ou deux lettres près, on te
      dit « presque » et on te montre où : un ь oublié n'est pas une erreur de vocabulaire.
      Un « presque » compte comme raté, c'est justement là que le mot doit revenir.
    </p>
  </Lecon>
);

export default function Recall({ cursive, toggleCursive }) {
  const [phase, setPhase] = useState('loading');
  const [mots, setMots] = useState([]);
  const [i, setI] = useState(0);
  const [essai, setEssai] = useState('');
  const [verdict, setVerdict] = useState(null);
  const [indice, setIndice] = useState(false);
  const [score, setScore] = useState({ ok: 0, presque: 0, total: 0 });
  const [rates, setRates] = useState([]);
  const [token] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  });
  const inputRef = useRef(null);

  const charger = useCallback(() => {
    const leave = () => { try { window.location.replace('/'); } catch {} };
    if (!token) { leave(); return; }
    setPhase('loading');
    fetch('/api/rappel/serie?n=15', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.status === 401 || r.status === 403) { leave(); return null; }
        if (!r.ok) throw new Error('http');
        return r.json();
      })
      .then((d) => {
        if (!d) return;
        setMots(d.mots || []);
        setI(0); setEssai(''); setVerdict(null); setIndice(false);
        setScore({ ok: 0, presque: 0, total: 0 }); setRates([]);
        setPhase('ready');
        setTimeout(() => inputRef.current?.focus(), 60);
      })
      .catch(() => setPhase('error'));
  }, [token]);

  useEffect(charger, [charger]);

  const m = mots[i];

  const valider = useCallback(() => {
    if (!m || verdict || !essai.trim()) return;
    fetch('/api/rappel/answer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ word_id: m.id, essai }),
    })
      // Une session expirée renvoie 403 avec un corps d'erreur : sans ce
      // contrôle, `d.ok` valait `undefined` et la réponse était comptée FAUSSE.
      // On préfère ne rien compter et renvoyer à l'accueil, comme partout.
      .then((r) => {
        if (r.status === 401 || r.status === 403) {
          try { window.location.replace('/'); } catch {}
          return null;
        }
        if (!r.ok) throw new Error('http');
        return r.json();
      })
      .then((d) => {
        if (d == null) return;
        setVerdict(d);
        setScore((s) => ({
          ok: s.ok + (d.ok ? 1 : 0),
          presque: s.presque + (d.presque ? 1 : 0),
          total: s.total + 1,
        }));
        if (!d.ok) setRates((x) => [...x, { ...m, russe: d.russe, essai, presque: d.presque }]);
      })
      .catch(() => {});
  }, [m, essai, verdict, token]);

  const suivant = useCallback(() => {
    setVerdict(null); setEssai(''); setIndice(false);
    if (i + 1 >= mots.length) setPhase('done');
    else { setI(i + 1); setTimeout(() => inputRef.current?.focus(), 60); }
  }, [i, mots.length]);

  if (phase === 'loading') {
    return (
      <Shell actif="/?rappel" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell actif="/?rappel" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="text-center flex flex-col items-center gap-4 pt-16">
          <p className="text-slate-400">Impossible de charger la série.</p>
          <button onClick={charger} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors">Réessayer</button>
        </div>
      </Shell>
    );
  }

  if (phase === 'done' || !m) {
    return (
      <Shell actif="/?rappel" cursive={cursive} toggleCursive={toggleCursive}>
        <h1 className="text-xl md:text-2xl font-bold">Série terminée</h1>
        {score.total > 0 ? (
          <p className="text-slate-300">
            {score.ok} produits sur {score.total}
            {score.presque > 0 && `, et ${score.presque} à une ou deux lettres près`}.
          </p>
        ) : (
          <p className="text-slate-400">
            Pas encore assez de mots mûrs pour cet exercice. Travaille quelques séries de quiz,
            ils reviendront ici quand tu les reconnaîtras.
          </p>
        )}
        {rates.length > 0 && (
          <div className="flex flex-col divide-y divide-slate-800 border border-slate-700 rounded-xl overflow-hidden bg-slate-800/40">
            {rates.map((r, k) => (
              <div key={k} className="px-4 py-2.5 flex items-baseline gap-3 flex-wrap">
                <span lang="ru" style={{ fontFamily: SERIF }} className="text-blue-300 text-lg">{r.russe}</span>
                <span className="text-sm text-slate-400">{r.sens}</span>
                <span className="ml-auto text-[11px] text-slate-500">
                  tu as écrit <span lang="ru" className="text-slate-300">{r.essai}</span>
                  {r.presque && ' · presque'}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={charger} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors">Une autre série</button>
          <a href="/?tuteur" className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg font-medium transition-colors">Retour au tuteur</a>
        </div>
      </Shell>
    );
  }

  return (
    <Shell actif="/?rappel" cursive={cursive} toggleCursive={toggleCursive}>
      <CoursRappel />

      <div className="flex items-baseline justify-between">
        <h1 className="text-xl md:text-2xl font-bold">Rappel actif</h1>
        <span className="text-xs text-slate-500 tabular-nums">
          {i + 1} / {mots.length}{score.total > 0 && ` · ${score.ok} produits`}
        </span>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 flex flex-col gap-4">
        <p className="text-sm text-slate-400">Écris ce mot en russe.</p>
        <p className="text-xl md:text-2xl text-white">{m.sens}</p>

        <input
          ref={inputRef}
          lang="ru"
          value={essai}
          onChange={(e) => setEssai(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') (verdict ? suivant() : valider()); }}
          disabled={!!verdict}
          spellCheck="false"
          autoComplete="off"
          placeholder="по-русски…"
          style={{ fontFamily: SERIF }}
          className={`w-full bg-slate-900 border rounded-xl px-4 py-3 text-lg text-white placeholder-slate-600 outline-none transition-colors ${
            verdict ? (verdict.ok ? 'border-green-500' : 'border-red-500') : 'border-slate-700 focus:border-blue-500'
          }`}
        />

        {!verdict && (
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={valider} disabled={!essai.trim()}
              className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-sm font-medium transition-colors">
              Valider
            </button>
            <button onClick={() => setIndice(true)} className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
              Un indice
            </button>
            {indice && (
              <span className="text-sm text-slate-400">
                <span lang="ru" style={{ fontFamily: SERIF }} className="text-blue-300">{m.initiale}</span>
                {' · '}{m.lettres} lettres
              </span>
            )}
          </div>
        )}

        {verdict && (
          <div className="flex flex-col gap-2 border-t border-slate-700 pt-3">
            <p className="text-sm">
              {verdict.ok ? (
                <span className="text-green-400">Juste.</span>
              ) : verdict.presque ? (
                <span className="text-amber-400">
                  Presque : {verdict.distance} lettre{verdict.distance > 1 ? 's' : ''} d'écart.
                </span>
              ) : (
                <span className="text-red-400">Non.</span>
              )}
              {!verdict.ok && (
                <>
                  {' '}
                  <span lang="ru" style={{ fontFamily: SERIF }} className="text-blue-300 text-lg">{verdict.russe}</span>
                </>
              )}
            </p>
            <button onClick={suivant} autoFocus
              className={`self-start px-5 py-2.5 rounded-lg font-medium transition-colors ${verdict.ok ? 'bg-blue-600 hover:bg-blue-500' : 'bg-slate-700 hover:bg-slate-600'}`}>
              {i + 1 >= mots.length ? 'Voir le bilan' : 'Suivant'}
            </button>
          </div>
        )}
      </div>
    </Shell>
  );
}
